import { useState, type ChangeEvent, type MouseEvent } from 'react';
import { HelperDueReminderLimitationModal } from './HelperDueReminderLimitationModal';

export type HelperDueReminderCopyContext = 'default' | 'medication';

type HelperDueReminderCheckboxProps = {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  t: (key: string) => string;
  /** `form` matches TaskFormPage checkbox rows; `wizard` matches task group schedule fields. */
  variant?: 'form' | 'wizard';
  /** Medication reminders use "medication time" wording in the limitation modal intro. */
  copyContext?: HelperDueReminderCopyContext;
};

export const HelperDueReminderCheckbox = ({
  id,
  checked,
  disabled = false,
  onChange,
  t,
  variant = 'form',
  copyContext = 'default',
}: HelperDueReminderCheckboxProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const limitationCopy = getHelperDueReminderLimitationCopy(t, copyContext);

  const handleInputClick = (e: MouseEvent<HTMLInputElement>): void => {
    if (disabled || checked) {
      return;
    }
    e.preventDefault();
    setModalOpen(true);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (disabled) {
      return;
    }
    if (!e.target.checked) {
      onChange(false);
    }
  };

  const handleConfirm = (): void => {
    onChange(true);
    setModalOpen(false);
  };

  const handleCancel = (): void => {
    onChange(false);
    setModalOpen(false);
  };

  const inputClassName =
    'h-5 w-5 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed';
  const labelTextClassName =
    variant === 'wizard'
      ? 'text-sm text-gray-800 dark:text-gray-100'
      : 'text-sm font-medium text-gray-700 dark:text-gray-200';

  const checkboxRow =
    variant === 'wizard' ? (
      <label
        className={`flex min-h-11 items-center gap-3 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      >
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onClick={handleInputClick}
          onChange={handleChange}
          className={inputClassName}
        />
        <span className={labelTextClassName}>{t('task.form.remindHelperBeforeDueLabel')}</span>
      </label>
    ) : (
      <div className={`flex items-center gap-3 ${disabled ? 'opacity-60' : ''}`}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onClick={handleInputClick}
          onChange={handleChange}
          className={inputClassName}
        />
        <label
          htmlFor={id}
          className={`${labelTextClassName} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {t('task.form.remindHelperBeforeDueLabel')}
        </label>
      </div>
    );

  return (
    <>
      {checkboxRow}
      {disabled ? (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('task.form.remindHelperBeforeDueDisabledHint')}
        </p>
      ) : null}
      <HelperDueReminderLimitationModal
        open={modalOpen}
        title={limitationCopy.title}
        introText={limitationCopy.introText}
        limitation1={limitationCopy.limitation1}
        limitation2={limitationCopy.limitation2}
        limitation3={limitationCopy.limitation3}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export const getHelperDueReminderLimitationCopy = (
  t: (key: string) => string,
  copyContext: HelperDueReminderCopyContext = 'default',
): {
  title: string;
  introText: string;
  limitation1: string;
  limitation2: string;
  limitation3: string;
} => ({
  title: t('task.form.remindHelperBeforeDueModalTitle'),
  introText:
    copyContext === 'medication'
      ? t('task.medication.remindHelperBeforeDueModalIntro')
      : t('task.form.remindHelperBeforeDueModalIntro'),
  limitation1: t('task.form.remindHelperBeforeDueModalLimit1'),
  limitation2: t('task.form.remindHelperBeforeDueModalLimit2'),
  limitation3: t('task.form.remindHelperBeforeDueModalLimit3'),
});
